/**
 * FeatureShowcase - Suggestion chips + demo access for the dashboard idle state
 *
 * Shows actionable example queries organized by research mode.
 * Clicking a chip pre-fills the research input.
 * Demo modes (debate, fact-check) are accessible via a subtle link.
 */

import { useState, useCallback, useEffect } from 'react';
import { BookOpen, TrendingUp, Target, Swords, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

// Demo question sets (kept for demo panel compatibility)
const DEMO_QUESTIONS = {
  bestAnswer: [
    "What's the best strategy for negotiating a salary raise?",
    "Should I invest in index funds or individual stocks?",
    "How do I improve my productivity while working remotely?",
    "What are the health benefits and risks of intermittent fasting?",
  ],
  debate: [
    "Is AI going to replace most knowledge workers in the next decade?",
    "Should governments regulate cryptocurrency more strictly?",
    "Is remote work better for productivity than office work?",
    "Will electric vehicles dominate the market by 2030?",
  ],
  factCheck: [
    "What percentage of startups actually fail in the first 5 years?",
    "How effective are current mRNA vaccines against COVID variants?",
    "What's the real environmental impact of Bitcoin mining?",
    "How much does the average American save for retirement?",
  ],
};

export type FeatureDemo = 'bestAnswer' | 'debate' | 'factCheck';

// Suggestion chips organized by mode
const SUGGESTION_CHIPS = [
  {
    mode: 'general' as const,
    icon: BookOpen,
    color: 'text-purple-400',
    hoverBg: 'hover:bg-purple-500/10 hover:border-purple-500/30',
    suggestions: [
      'Health effects of intermittent fasting',
      'Best negotiation strategies for salary',
    ],
  },
  {
    mode: 'financial' as const,
    icon: TrendingUp,
    color: 'text-green-400',
    hoverBg: 'hover:bg-green-500/10 hover:border-green-500/30',
    suggestions: [
      'NVDA earnings analysis',
      'TSLA vs RIVN',
    ],
  },
  {
    mode: 'strategic' as const,
    icon: Target,
    color: 'text-blue-400',
    hoverBg: 'hover:bg-blue-500/10 hover:border-blue-500/30',
    suggestions: [
      'Product-market fit for AI coding tools',
    ],
  },
];

interface FeatureShowcaseProps {
  /** Callback when user clicks a suggestion chip or demo feature */
  onFeatureClick?: (feature: FeatureDemo, question: string, mode?: 'general' | 'financial' | 'strategic') => void;
  onHoverFeature?: (feature: FeatureDemo | null) => void;
  isSubmitting?: boolean;
  className?: string;
  /** Whether to show demo links (debate/fact-check) */
  showDemos?: boolean;
}

export function FeatureShowcase({
  onFeatureClick,
  isSubmitting = false,
  className,
  showDemos = true,
}: FeatureShowcaseProps) {
  const [showDemoOptions, setShowDemoOptions] = useState(false);

  const handleChipClick = useCallback((query: string, mode?: 'general' | 'financial' | 'strategic') => {
    if (isSubmitting || !onFeatureClick) return;
    onFeatureClick('bestAnswer', query, mode);
  }, [isSubmitting, onFeatureClick]);

  const handleDemoClick = useCallback((feature: FeatureDemo) => {
    if (isSubmitting || !onFeatureClick) return;
    const questions = DEMO_QUESTIONS[feature];
    const randomQuestion = questions[Math.floor(Math.random() * questions.length)];
    onFeatureClick(feature, randomQuestion);
  }, [isSubmitting, onFeatureClick]);

  // Reset demo panel when not submitting
  useEffect(() => {
    if (!isSubmitting) {
      setShowDemoOptions(false);
    }
  }, [isSubmitting]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Suggestion chips by mode */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {SUGGESTION_CHIPS.map((group) => {
          const Icon = group.icon;
          return group.suggestions.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => handleChipClick(suggestion, group.mode)}
              disabled={isSubmitting || !onFeatureClick}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full',
                'text-xs text-text-secondary border border-border',
                'transition-all duration-200',
                group.hoverBg,
                'hover:text-text-primary',
                'disabled:opacity-40 disabled:cursor-not-allowed'
              )}
            >
              <Icon className={cn('w-3 h-3', group.color)} />
              <span>{suggestion}</span>
            </button>
          ));
        })}
      </div>

      {/* Demo access — subtle, not primary */}
      {showDemos && onFeatureClick && (
        <div className="text-center">
          {!showDemoOptions ? (
            <button
              onClick={() => setShowDemoOptions(true)}
              className="text-[11px] text-text-muted hover:text-text-secondary transition-colors"
            >
              or try a live demo
            </button>
          ) : (
            <div className="flex items-center justify-center gap-3 animate-fadeIn">
              <button
                onClick={() => handleDemoClick('debate')}
                disabled={isSubmitting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary border border-border hover:border-border-light rounded-full transition-all duration-200"
              >
                <Swords className="w-3 h-3 text-red-400/70" />
                Watch AI debate
              </button>
              <button
                onClick={() => handleDemoClick('factCheck')}
                disabled={isSubmitting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary border border-border hover:border-border-light rounded-full transition-all duration-200"
              >
                <ShieldCheck className="w-3 h-3 text-blue-400/70" />
                See fact-checking
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Export demo questions for use in demo panels
export { DEMO_QUESTIONS };
